import { Card, createStyles, Group, Stack } from '@mantine/core';
import { useMobxReaction } from '@repo/common/utils/useMobxReaction';
import { MultiSelect, Select, Switch, Text } from '@repo/foundations';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { BlockHeader } from '../../components/BlockHeader/BlockHeader';
import {
	propagateLevelOptions,
	propagateMetadataOptions,
	propagateToOptions,
} from './constants';
import { PropagateBlockStore } from './store';
import type { PropagateMetadataSettings } from './types';

interface PropagateBlockControllerInternalProps {
	store: PropagateBlockStore;
	includeTitle?: boolean;
	onPropagateDelete: () => void;
	tooltip?: string;
}

const useStyles = createStyles((theme) => ({
	switchGroup: {
		display: 'flex',
		justifyContent: 'space-between',
	},
	switchGroupContent: {
		gap: 0,
	},
	group: {
		gap: theme.spacing.sm,
		padding: 0,
		width: '100%',
	},
	selectGroup: {
		gap: theme.spacing.md,
	},
	card: {
		overflow: 'visible',
	},
}));

const PropagateBlockControllerInternal = observer(
	({
		store,
		onPropagateDelete,
		includeTitle = true,
		tooltip,
	}: PropagateBlockControllerInternalProps) => {
		const { classes } = useStyles();

		return (
			<Card withBorder radius="md" pt={0} className={classes.card}>
				{includeTitle && (
					<BlockHeader
						title="Propagate property metadata"
						iconName="cornerDownRightDouble"
						onDelete={onPropagateDelete}
						toolTip={tooltip ?? 'Remove propagate'}
						backgroundColor="fill/transparent-secondary/default"
					/>
				)}
				<Card.Section p="md">
					<Group className={classes.selectGroup}>
						<MultiSelect
							label="Properties"
							size="sm"
							value={store.propagateSettings.properties}
							setValue={(properties) => store.addProperties(properties)}
							data={propagateMetadataOptions}
						/>
						<Group className={classes.group}>
							<Select
								label="Propagate to"
								variant="default"
								size="md"
								value={store.propagateSettings.propagateTo ?? 'Children'}
								defaultValue="Children"
								onChange={(value) => store.setPropagateTo(value)}
								data={propagateToOptions}
								width="48.5%"
								dropdownPosition="bottom"
							/>
							<Select
								label="Up to"
								variant="default"
								size="md"
								value={store.propagateSettings.level ?? '1'}
								defaultValue={'1'}
								onChange={(value) => store.setLevel(value)}
								data={propagateLevelOptions}
								width="48.5%"
								dropdownPosition="bottom"
							/>
						</Group>
					</Group>
				</Card.Section>
				<Card.Section p="md" withBorder>
					<Group position="apart" align="center" noWrap>
						<Stack spacing={0}>
							<Text size="sm">Override existing properties</Text>
							<Text size="xs" variant="tertiary" color="text/secondary/default">
								When disabled, properties with pre-existing values will not be
								updated by the automation.
							</Text>
						</Stack>
						<Switch
							checked={store.propagateSettings.overrideExistingProperties}
							onChange={(event) =>
								store.setOverrideExistingProperties(event.currentTarget.checked)
							}
						/>
					</Group>
				</Card.Section>
			</Card>
		);
	}
);

interface PropagateBlockControllerProps
	extends Pick<
		PropagateBlockControllerInternalProps,
		'onPropagateDelete' | 'tooltip'
	> {
	onPropagateChange: (propagateMetadata: PropagateMetadataSettings) => void;
	initialPropagateSettings: PropagateMetadataSettings;
}

export const PropagateBlockController = observer(
	({
		onPropagateChange,
		initialPropagateSettings,
		...rest
	}: PropagateBlockControllerProps) => {
		const [store] = useState(
			() => new PropagateBlockStore(initialPropagateSettings)
		);

		useMobxReaction(
			useCallback(() => store.propagateSettings, [store]),
			useCallback(
				(propagateSettings) => {
					onPropagateChange(toJS(propagateSettings));
				},
				[onPropagateChange]
			)
		);

		return <PropagateBlockControllerInternal {...rest} store={store} />;
	}
);
